import React, {
    forwardRef, useImperativeHandle, useRef, useState
} from 'react';
import {
    Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter
} from 'shared/components/ui/card';
import {
    Label
} from 'shared/components/ui/label';
import {
    Input
} from 'shared/components/ui/input';
import {
    Link
} from 'react-router-dom';
import {
    Button
} from 'shared/components/ui/button';
import {
    Turnstile
} from '@marsidev/react-turnstile';

const LoginForm = forwardRef((props, ref) => {
    const {
        loading, onSubmit, admin
    } = props;

    const emailRef = useRef(null);
    const passwordRef = useRef(null);
    const turnstileRef = useRef(null);
    const [turnstileToken, setTurnstileToken] = useState(null);

    useImperativeHandle(ref, () => ({
        getEmailRef: () => emailRef.current,
        getPasswordRef: () => passwordRef.current,
        getTurnstileToken: () => turnstileToken,
        // Turnstile tokens are SINGLE-USE — the backend spends one per verify.
        // After a failed submit the widget still holds the spent token, so a
        // retry replays a dead token and is rejected (surfacing as a credential
        // error until the page is reloaded). Reset the widget to mint a fresh
        // token and disable the button until it arrives.
        resetTurnstile: () => {
            setTurnstileToken(null);
            turnstileRef.current?.reset();
        },
    }));

    return (
        <Card className="w-full max-w-[500px]">
            <CardHeader>
                <CardTitle className="text-2xl">Login</CardTitle>
                <CardDescription>Enter your email and password to sign in to your account.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <form onSubmit={onSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input ref={emailRef} name='email' id="email" type="email" placeholder="m@example.com" required />
                    </div>
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label htmlFor="password">Password</Label>
                            {!admin && <Link
                                to="/auth/reset-password"
                                className="text-sm font-medium text-[#B1092A] underline underline-offset-2 hover:text-[#8d0721] focus:text-[#8d0721]"
                            >
                                Forgot password?
                            </Link>}
                        </div>
                        <Input ref={passwordRef} name='password' id="password" type="password" required />
                    </div>
                    <Turnstile
                        ref={turnstileRef}
                        siteKey={process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY}
                        onSuccess={setTurnstileToken}
                        onExpire={() => setTurnstileToken(null)}
                        onError={() => setTurnstileToken(null)}
                    />
                    <Button disabled={loading || !turnstileToken} name='button' onSubmit={onSubmit} variant='default' className="w-full">Sign in</Button>
                </form>
            </CardContent>
            {!admin && <CardFooter className='flex flex-col'>
                <div className="mt-4 text-center text-sm">
                    Don&apos;t have an account?{' '}
                    <Link
                        to="/auth/signup"
                        className="font-medium text-[#B1092A] underline underline-offset-2 hover:text-[#8d0721] focus:text-[#8d0721]"
                    >
                        Sign up
                    </Link>
                </div>
            </CardFooter>}
        </Card>
    );
});

export default LoginForm;
